import { Injectable, EnvironmentInjector, inject, createEnvironmentInjector, Injector } from '@angular/core';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { BottomPaneRef } from '../ui/bottom-pane/bottom-pane.ref';
import { BottomPaneConfig } from '../ui/bottom-pane/bottom-pane.types';
import { BOTTOM_PANE_DATA, BOTTOM_PANE_ID, BOTTOM_PANE_STATE } from '../ui/bottom-pane/bottom-pane.tokens';
import { BottomPaneShellComponent } from '../ui/bottom-pane/bottom-pane.shell';


let AUTO_ID = 0;

@Injectable({ providedIn: 'root' })
export class BottomPaneService {
  private readonly overlay = inject(Overlay);
  private readonly rootInjector = inject(EnvironmentInjector);
  private readonly elementInjector = inject(Injector);
  private readonly panes = new Map<string, BottomPaneRef<any>>();

  open<TData = unknown, TResult = unknown>(config: BottomPaneConfig<TData>) {
    const id = config.id ?? `bottom-pane-${++AUTO_ID}`;
    if (this.panes.has(id)) {
      throw new Error(`BottomPane with id "${id}" already exists.`);
    }

    const overlayRef = this.createOverlay(config);
    const paneRef = new BottomPaneRef<TResult>(id, overlayRef);

    // Z-index
    const zIndex = config.zIndex ?? this.nextZIndex();
    paneRef.setZIndex(zIndex);

    // Content injector
    const contentInjector = createEnvironmentInjector([
      { provide: BOTTOM_PANE_ID, useValue: id },
      { provide: BOTTOM_PANE_DATA, useValue: config.data },
      { provide: BOTTOM_PANE_STATE, useValue: paneRef.state },
      { provide: BottomPaneRef, useValue: paneRef },
    ], this.rootInjector);

    // Shell injector
    const shellInjector = createEnvironmentInjector([
      { provide: BOTTOM_PANE_ID, useValue: id },
      { provide: BOTTOM_PANE_STATE, useValue: paneRef.state },
      { provide: BottomPaneRef, useValue: paneRef },
    ], this.rootInjector);

    const shellPortal = new ComponentPortal(BottomPaneShellComponent, null, shellInjector);
    const shellRef = overlayRef.attach(shellPortal);

    const contentPortal = new ComponentPortal(config.component as any, null, contentInjector);

    shellRef.instance.contentPortal = contentPortal;
    shellRef.instance.config = config;

    shellRef.instance.contentAttached.subscribe(instance => {
      paneRef.componentInstance = instance;
    });

    shellRef.changeDetectorRef.detectChanges();

    if (!config.disableClose) {
      overlayRef.keydownEvents().subscribe(e => {
        if (e.key === 'Escape') paneRef.close();
      });
      if (config.hasBackdrop && config.closeOnBackdropClick) {
        overlayRef.backdropClick().subscribe(() => paneRef.closeClick());
      }
    }

    overlayRef.detachments().subscribe(() => this.panes.delete(id));
    this.panes.set(id, paneRef);
    return paneRef;
  }

  close(id: string) {
    this.panes.get(id)?.close();
  }

  closeAll() {
    [...this.panes.values()].forEach(p => p.close());
  }

  list() {
    return [...this.panes.values()];
  }

  bringToFront(id: string) {
    const maxZ = this.maxZIndex();
    this.panes.get(id)?.bringToFront(maxZ);
  }

  // helpers
  private createOverlay(config: BottomPaneConfig): OverlayRef {
    const overlayConfig = new OverlayConfig({
      hasBackdrop: !!config.hasBackdrop,
      backdropClass: config.backdropClass ?? 'tt-backdrop',
      panelClass: config.panelClass,
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      positionStrategy: this.overlay.position()
        .global()
        //.centerVertically(),
        .centerHorizontally()
        // .left('0')
        //  .right('0')
        .bottom('0'),
      width: config.width ?? '100%',
      height: config.height ?? '100%',
      minHeight: config.minHeight ?? '40vh',
      maxHeight: config.maxHeight ?? '95vh',

    });
    return this.overlay.create(overlayConfig);
  }

  private nextZIndex(): number {
    const currentMax = this.maxZIndex();
    return currentMax ? currentMax + 1 : 1100;
  }

  private maxZIndex(): number {
    let max = 0;
    for (const p of this.panes.values()) {
      const z = parseInt((p as any)['overlayRef']?.hostElement?.style?.zIndex || '0', 10);
      if (!Number.isNaN(z)) max = Math.max(max, z);
    }
    return max;
  }
}